/* =============================================================================
   EOLUX — global behaviour
   Vanilla, progressive-enhancement only. Every initialiser is idempotent and
   scoped to a container so it can re-run on shopify:section:load without
   double-binding.
   ========================================================================== */
(function () {
  'use strict';

  const EOLUX = (window.EOLUX = window.EOLUX || {});
  const focusable =
    'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

  /* --------------------------------------------------------------- helpers */
  const once = (el, key) => {
    if (!el || el.dataset[key] === 'true') return false;
    el.dataset[key] = 'true';
    return true;
  };

  const formatMoney = (cents) => {
    const format = EOLUX.moneyFormat || '{{amount_with_comma_separator}}€';
    const value = (cents / 100).toFixed(2);
    const [int, dec] = value.split('.');
    const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return format
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, `${grouped},${dec}`)
      .replace(/\{\{\s*amount\s*\}\}/g, `${int.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${dec}`)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, grouped);
  };
  EOLUX.formatMoney = formatMoney;

  const headerCleanups = new WeakMap();

  function initHeader(header) {
    if (!header.classList.contains('header--sticky') || headerCleanups.has(header)) return;
    const shell = header.closest('.shopify-section');
    if (!shell) return;
    const controller = new AbortController();
    const { signal } = controller;
    let lastScrollY = Math.max(0, window.scrollY);
    let scrollFrame = 0;

    const update = (forceVisible = false) => {
      const scrollY = Math.max(0, window.scrollY);
      const delta = scrollY - lastScrollY;
      const threshold = shell.getBoundingClientRect().top + scrollY + header.offsetHeight;
      const surfaceActive = Boolean(
        header.querySelector('[aria-expanded="true"]') || document.querySelector('[data-drawer][open]')
      );
      header.classList.toggle('is-surface-active', surfaceActive);

      if (scrollY <= threshold) {
        header.classList.remove('is-sticky', 'is-header-hidden');
      } else {
        header.classList.add('is-sticky');
        const keyboardFocus = header.contains(document.activeElement) && document.activeElement.matches(':focus-visible');
        if (forceVisible || surfaceActive || keyboardFocus || delta < -4) {
          header.classList.remove('is-header-hidden');
        } else if (delta > 4) {
          header.classList.add('is-header-hidden');
        }
      }
      if (Math.abs(delta) > 4 || forceVisible) lastScrollY = scrollY;
    };

    const queueUpdate = () => {
      if (scrollFrame) return;
      scrollFrame = requestAnimationFrame(() => {
        scrollFrame = 0;
        update();
      });
    };
    const measure = () => {
      shell.style.setProperty('--sticky-header-height', `${header.offsetHeight}px`);
      update(true);
    };
    const reveal = () => update(true);
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(header);
    const disclosureObserver = new MutationObserver(queueUpdate);
    disclosureObserver.observe(header, { subtree: true, attributes: true, attributeFilter: ['aria-expanded'] });
    window.addEventListener('scroll', queueUpdate, { passive: true, signal });
    window.addEventListener('resize', measure, { passive: true, signal });
    header.addEventListener('focusin', reveal, { signal });
    document.addEventListener('drawer:open', reveal, { signal });
    document.addEventListener('drawer:close', reveal, { signal });
    shell.addEventListener('shopify:section:select', reveal, { signal });
    measure();

    headerCleanups.set(header, () => {
      controller.abort();
      resizeObserver.disconnect();
      disclosureObserver.disconnect();
      cancelAnimationFrame(scrollFrame);
      header.classList.remove('is-sticky', 'is-header-hidden', 'is-surface-active');
      shell.style.removeProperty('--sticky-header-height');
      headerCleanups.delete(header);
    });
  }

  /* --------------------------------------------------------------- drawers */
  const Drawer = {
    lastFocus: null,

    open(drawer) {
      if (!drawer) return;
      Drawer.lastFocus = document.activeElement;
      drawer.hidden = false;
      drawer.setAttribute('open', '');
      document.body.classList.add('overflow-hidden');
      document.querySelectorAll(`[aria-controls="${drawer.id}"]`).forEach((t) => {
        t.setAttribute('aria-expanded', 'true');
      });
      const target = drawer.querySelector('input:not([type="hidden"]), ' + focusable);
      if (target) window.requestAnimationFrame(() => target.focus());
      drawer.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    },

    close(drawer) {
      if (!drawer || drawer.hidden) return;
      drawer.hidden = true;
      drawer.removeAttribute('open');
      if (!document.querySelector('[data-drawer][open]')) {
        document.body.classList.remove('overflow-hidden');
      }
      document.querySelectorAll(`[aria-controls="${drawer.id}"]`).forEach((t) => {
        t.setAttribute('aria-expanded', 'false');
      });
      if (Drawer.lastFocus && document.contains(Drawer.lastFocus)) Drawer.lastFocus.focus();
      drawer.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    },

    closeAll() {
      document.querySelectorAll('[data-drawer][open]').forEach(Drawer.close);
    },
  };
  EOLUX.Drawer = Drawer;

  function initDrawer(drawer) {
    if (!once(drawer, 'drawerReady')) return;

    drawer.addEventListener('click', (e) => {
      if (e.target.closest('[data-drawer-close]')) {
        e.preventDefault();
        Drawer.close(drawer);
      }
    });

    // Focus trap while open.
    drawer.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab' || drawer.hidden) return;
      const items = [...drawer.querySelectorAll(focusable)].filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });
  }

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      Drawer.closeAll();
      document.querySelectorAll('[data-dropdown-toggle][aria-expanded="true"]').forEach(closeDropdown);
    }
  });

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-menu-toggle], [data-search-toggle], [data-cart-toggle], [data-facets-toggle]');
    if (!trigger) return;

    // The cart icon stays a real link when the drawer is disabled.
    if (trigger.matches('[data-cart-toggle]') && EOLUX.cartType !== 'drawer') return;

    const id = trigger.getAttribute('aria-controls') || 'CartDrawer';
    const drawer = document.getElementById(id);
    if (!drawer) return;
    e.preventDefault();
    drawer.hidden ? Drawer.open(drawer) : Drawer.close(drawer);
  });

  /* ------------------------------------------------------------- dropdowns */
  const dropdownCleanups = new WeakMap();

  function closeDropdown(toggle) {
    toggle.setAttribute('aria-expanded', 'false');
    const panel = document.getElementById(toggle.getAttribute('aria-controls'));
    if (panel) panel.hidden = true;
    const header = toggle.closest('[data-header]');
    header?.classList.toggle('is-surface-active', Boolean(header.querySelector('[aria-expanded="true"]') || document.querySelector('[data-drawer][open]')));
  }

  function initDropdown(item) {
    if (dropdownCleanups.has(item)) return;
    const toggle = item.querySelector('[data-dropdown-toggle]');
    const panel = toggle && document.getElementById(toggle.getAttribute('aria-controls'));
    if (!toggle || !panel) return;
    const shell = item.closest('.shopify-section');
    const header = item.closest('[data-header]');
    const mobileToggle = shell.querySelector(`[data-mega-mobile-toggle][aria-controls="${panel.id}"]`);
    const mobileSlot = shell.querySelector(`[data-mega-mobile-slot="${panel.id}"]`);
    const mobile = matchMedia('(max-width: 1199px)');
    const hover = matchMedia('(hover: hover)');
    const controller = new AbortController();
    const { signal } = controller;
    let closeTimer = 0;
    let openedByHover = false;
    let editorSelected = false;
    const activeToggle = () => mobile.matches && mobileToggle ? mobileToggle : toggle;
    const close = () => {
      clearTimeout(closeTimer);
      openedByHover = false;
      if (mobileToggle) mobileToggle.setAttribute('aria-expanded', 'false');
      closeDropdown(toggle);
    };
    const open = (fromHover = false) => {
      clearTimeout(closeTimer);
      if (!mobile.matches) {
        document.querySelectorAll('[data-dropdown-toggle][aria-expanded="true"]').forEach((other) => {
          if (other !== toggle) closeDropdown(other);
        });
      }
      openedByHover = fromHover;
      activeToggle().setAttribute('aria-expanded', 'true');
      panel.hidden = false;
      header.classList.add('is-surface-active');
      header.classList.remove('is-header-hidden');
    };
    toggle.addEventListener('click', () => {
      toggle.getAttribute('aria-expanded') === 'true' && !openedByHover ? close() : open();
    }, { signal });
    mobileToggle?.addEventListener('click', () => panel.hidden ? open() : close(), { signal });
    item.addEventListener('pointerenter', (e) => {
      if (!mobile.matches && hover.matches && e.pointerType !== 'touch') open(true);
    }, { signal });
    item.addEventListener('pointerleave', () => {
      if (mobile.matches || editorSelected || panel.contains(document.activeElement)) return;
      closeTimer = setTimeout(close, 160);
    }, { signal });
    panel.addEventListener('pointerenter', () => clearTimeout(closeTimer), { signal });
    item.addEventListener('focusout', (e) => {
      if (!mobile.matches && !item.contains(e.relatedTarget)) close();
    }, { signal });
    const keydown = (e) => {
      if (e.key === 'Escape' && !panel.hidden) {
        e.stopPropagation();
        e.preventDefault();
        close();
        activeToggle().focus();
      } else if (e.key === 'ArrowDown' && e.target === activeToggle()) {
        e.preventDefault();
        open();
        panel.querySelector(focusable)?.focus();
      }
    };
    item.addEventListener('keydown', keydown, { signal });
    mobileSlot?.addEventListener('keydown', keydown, { signal });
    mobileToggle?.addEventListener('keydown', keydown, { signal });
    document.addEventListener('pointerdown', (e) => {
      if (!mobile.matches && !item.contains(e.target)) close();
    }, { signal });
    const placePanel = () => {
      close();
      if (mobileSlot) (mobile.matches ? mobileSlot : item).append(panel);
      if (!mobile.matches && mobileSlot) {
        const drawer = mobileSlot.closest('[data-drawer]');
        if (drawer && !drawer.hidden) Drawer.close(drawer);
      }
    };
    mobile.addEventListener('change', placePanel, { signal });
    shell.addEventListener('shopify:block:select', (e) => {
      const selected = [...panel.querySelectorAll('[data-mega-block]')].find((block) => block.dataset.megaBlock === e.detail?.blockId || block.contains(e.target));
      if (!selected) return;
      editorSelected = true;
      if (mobile.matches && mobileSlot) Drawer.open(mobileSlot.closest('[data-drawer]'));
      open();
      selected.scrollIntoView({ block: 'nearest', behavior: 'instant' });
    }, { signal });
    shell.addEventListener('shopify:block:deselect', () => {
      editorSelected = false;
      close();
    }, { signal });
    document.addEventListener('drawer:open', (e) => {
      if (e.target !== mobileSlot?.closest('[data-drawer]')) close();
    }, { signal });
    placePanel();
    dropdownCleanups.set(item, () => {
      close();
      controller.abort();
      if (mobileSlot) item.append(panel);
      dropdownCleanups.delete(item);
    });
  }

  /* ------------------------------------------------------------ accordions */
  function initAccordion(trigger) {
    if (!once(trigger, 'accordionReady')) return;
    const panel = document.getElementById(trigger.getAttribute('aria-controls'));
    if (!panel) return;

    trigger.addEventListener('click', () => {
      const open = trigger.getAttribute('aria-expanded') === 'true';
      trigger.setAttribute('aria-expanded', String(!open));
      panel.toggleAttribute('data-open', !open);
    });
  }

  /* ----------------------------------------------------------------- cart */
  const Cart = {
    // Shopify answers 409 Conflict when two cart writes overlap, so every
    // request queues behind the last one instead of racing it. A shopper who
    // double-taps, or adds from a card while the drawer is still refreshing,
    // would otherwise see one of the two clicks fail.
    _queue: Promise.resolve(),

    request(url, body) {
      const run = () => Cart._send(url, body);
      const next = Cart._queue.then(run, run);
      // Keep the chain alive whatever this link does.
      Cart._queue = next.catch(() => {});
      return next;
    },

    async _send(url, body, retried) {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });

      // The AJAX cart answers with JSON. An HTML body means the request was
      // answered by something else — a market or locale redirect, an expired
      // session, bot protection, or the theme editor intercepting the POST.
      // Reading it as JSON throws `Unexpected token '<'`, which tells a shopper
      // nothing, so the response is parsed defensively and reported as a cart
      // failure with the URL kept in the console for whoever is debugging.
      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        console.error('Cart: expected JSON from', url, '- got', text.slice(0, 200));
        throw new Error(EOLUX.strings.cartError);
      }

      if (!res.ok) {
        // A conflict means someone else got there first, not that the add is
        // invalid — one retry resolves it.
        if (res.status === 409 && !retried) return Cart._send(url, body, true);
        throw new Error(data.description || data.message || data.error || EOLUX.strings.cartError);
      }
      return data;
    },

    /** Adds a line and refreshes the drawer from the section rendering payload. */
    async add(formData) {
      const body = { items: [{ id: formData.get('id'), quantity: Number(formData.get('quantity') || 1) }] };
      const sellingPlan = formData.get('selling_plan');
      if (sellingPlan) body.items[0].selling_plan = sellingPlan;

      const properties = {};
      formData.forEach((value, key) => {
        const match = /^properties\[(.+)\]$/.exec(key);
        if (match && value) properties[match[1]] = value;
      });
      if (Object.keys(properties).length) body.items[0].properties = properties;

      body.sections = ['cart-drawer'];
      const data = await Cart.request(`${EOLUX.routes.cart_add}.js`, body);
      Cart.render(data.sections);
      return data;
    },

    /** Quantity changes always address the line by `key`, never by variant id. */
    async change(key, quantity) {
      const data = await Cart.request(`${EOLUX.routes.cart_change}.js`, {
        id: key,
        quantity,
        sections: ['cart-drawer'],
      });
      Cart.render(data.sections, data);
      return data;
    },

    render(sections, cart) {
      if (sections && sections['cart-drawer']) {
        const parsed = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
        const fresh = parsed.querySelector('[data-cart-drawer]');
        const current = document.querySelector('[data-cart-drawer]');
        if (fresh && current) {
          const wasOpen = !current.hidden;
          current.replaceWith(fresh);
          initSection(document);
          if (wasOpen) Drawer.open(document.querySelector('[data-cart-drawer]'));
        }
      }
      if (cart) Cart.updateCount(cart.item_count);
      else Cart.refreshCount();
    },

    async refreshCount() {
      try {
        const res = await fetch(`${EOLUX.routes.cart}.js`);
        const cart = await res.json();
        Cart.updateCount(cart.item_count);
      } catch (e) {
        /* count stays as rendered */
      }
    },

    updateCount(count) {
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = count;
        el.hidden = false;
      });
    },
  };
  EOLUX.Cart = Cart;

  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[action*="/cart/add"]');
    if (!form) return;
    const button = form.querySelector('[data-add-to-cart]');
    if (!button) return; // let non-ajax forms through

    e.preventDefault();
    // The button marks itself busy below but nothing was reading it back, so a
    // second tap fired a second POST straight into the first one.
    if (button.getAttribute('aria-disabled') === 'true') return;
    const label = button.querySelector('[data-add-to-cart-text]');
    const original = label ? label.textContent : '';
    button.setAttribute('aria-disabled', 'true');

    try {
      await Cart.add(new FormData(form));
      if (EOLUX.cartType === 'drawer') {
        const drawer = document.querySelector('[data-cart-drawer]');
        if (drawer) Drawer.open(drawer);
      } else {
        window.location.href = EOLUX.routes.cart;
      }
    } catch (err) {
      if (label) label.textContent = err.message || EOLUX.strings.cartError;
      setTimeout(() => {
        if (label) label.textContent = original;
      }, 2600);
    } finally {
      button.removeAttribute('aria-disabled');
    }
  });

  document.addEventListener('click', async (e) => {
    const button = e.target.closest('[data-quantity-change]');
    if (!button) return;
    const line = button.closest('[data-line-key]');
    if (!line) return;
    e.preventDefault();
    line.style.opacity = '0.5';
    try {
      await Cart.change(line.dataset.lineKey, Number(button.dataset.quantityChange));
    } catch (err) {
      line.style.opacity = '';
    }
  });

  document.addEventListener('change', async (e) => {
    const input = e.target.closest('[data-quantity-input]');
    if (!input) return;
    const line = input.closest('[data-line-key]');
    if (!line) return;
    await Cart.change(line.dataset.lineKey, Math.max(0, Number(input.value)));
  });

  /* ------------------------------------------------------------ carousels */
  /**
   * Slide carousel used by the testimonial and device sections. Slides are
   * plain elements toggled with [hidden], so the content is server-rendered and
   * works without JS (the first slide simply stays visible).
   */
  function initCarousel(root) {
    if (!once(root, 'carouselReady')) return;

    const slides = [...root.querySelectorAll('[data-carousel-slide]')];
    const media = [...root.querySelectorAll('[data-carousel-media]')];
    // Optional third track: an edge preview of the slide the arrow leads to.
    const peeks = [...root.querySelectorAll('[data-carousel-peek]')];
    const prev = root.querySelector('[data-carousel-prev]');
    const next = root.querySelector('[data-carousel-next]');
    if (slides.length < 2) {
      if (prev) prev.hidden = true;
      if (next) next.hidden = true;
      return;
    }

    let index = Math.max(0, slides.findIndex((s) => !s.hidden));

    const show = (i) => {
      index = (i + slides.length) % slides.length;
      slides.forEach((s, n) => { s.hidden = n !== index; });
      media.forEach((m, n) => { m.hidden = n !== index; });
      peeks.forEach((p, n) => { p.hidden = n !== index; });
      root.dispatchEvent(new CustomEvent('carousel:change', { detail: { index }, bubbles: true }));
    };

    if (prev) prev.addEventListener('click', () => show(index - 1));
    if (next) next.addEventListener('click', () => show(index + 1));

    root.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') show(index - 1);
      if (e.key === 'ArrowRight') show(index + 1);
    });

    show(index);
    root.carouselShow = show;
  }

  /**
   * Footer columns are collapsible on narrow screens and always open on wide
   * ones. The control keeps its markup either way, so `aria-expanded` is synced
   * to what is actually visible rather than left lying.
   */
  function initFooterMenus(footer) {
    if (!once(footer, 'footerMenusReady')) return;
    const query = window.matchMedia('(min-width: 990px)');
    const sync = () => {
      footer.querySelectorAll('.footer__toggle').forEach((toggle) => {
        const panel = document.getElementById(toggle.getAttribute('aria-controls'));
        if (query.matches) {
          toggle.setAttribute('aria-expanded', 'true');
          toggle.setAttribute('aria-disabled', 'true');
        } else {
          toggle.removeAttribute('aria-disabled');
          toggle.setAttribute('aria-expanded', String(!!(panel && panel.hasAttribute('data-open'))));
        }
      });
    };
    sync();
    query.addEventListener('change', sync);
  }

  function syncHowToUseLinks(excludedSection = null) {
    const guides = [...document.querySelectorAll('[data-how-to-use]')].filter((guide) =>
      !excludedSection?.contains(guide) && !guide.closest('[hidden]') && guide.getClientRects().length > 0
    );
    document.querySelectorAll('[data-how-to-use-link]').forEach((link) => {
      const guide = guides.find((item) => item.dataset.howToUse === link.dataset.howToUseLink);
      link.hidden = !guide;
      if (guide) link.setAttribute('href', `#${guide.id}`);
      else link.removeAttribute('href');
    });
  }

  /* ---------------------------------------------------------------- product */
  /**
   * Variant selection. A change to any option recomputes the matching variant
   * and cascades it through the whole page: form input, price, availability,
   * button label, gallery, sticky bar and the URL.
   */
  function initProduct(root) {
    if (!once(root, 'productReady')) return;

    const picker = root.querySelector('[data-variant-picker]');
    const dataEl = picker && picker.querySelector('[data-variant-data]');
    if (!picker || !dataEl) return;

    let variants = [];
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }

    const priceEl = root.querySelector('.product-price');
    const buttons = [...root.querySelectorAll('[data-add-to-cart]')];
    const idInputs = [...document.querySelectorAll('[data-variant-id]')];

    const selectedOptions = () =>
      [...picker.querySelectorAll('.variant-option')].map((group) => {
        const checked = group.querySelector('.variant-option__input:checked');
        return checked ? checked.value : null;
      });

    function render(variant) {
      idInputs.forEach((input) => { input.value = variant ? variant.id : ''; });

      // Reflect the chosen value next to the option label.
      picker.querySelectorAll('.variant-option').forEach((group) => {
        const checked = group.querySelector('.variant-option__input:checked');
        const out = group.querySelector('[data-option-value]');
        if (out && checked) out.textContent = checked.value;
      });

      if (priceEl) {
        const current = priceEl.querySelector('.price__current');
        const compare = priceEl.querySelector('.price__compare');
        const compareValue = compare && compare.querySelector('.price__compare-value');
        const savings = priceEl.querySelector('.price__savings');
        if (variant && current) current.textContent = formatMoney(variant.price);
        const metafieldCompare = Number(priceEl.dataset.originalPrice || 0);
        const variantCompare = variant && Number(variant.compare_at_price || 0);
        const comparePrice = variantCompare > variant?.price ? variantCompare : metafieldCompare;
        const onSale = !!(variant && comparePrice > variant.price);
        if (compare) {
          compare.hidden = !onSale;
          if (onSale && compareValue) compareValue.textContent = formatMoney(comparePrice);
        }
        if (savings) {
          savings.hidden = !onSale;
          if (onSale) {
            const amount = formatMoney(comparePrice - variant.price);
            const percent = Math.floor(((comparePrice - variant.price) * 100) / comparePrice);
            savings.textContent = priceEl.dataset.savingsTemplate
              .replace('__AMOUNT__', amount)
              .replace('__PERCENT__', percent);
          }
        }
        priceEl.classList.toggle('price--on-sale', onSale);
      }

      buttons.forEach((button) => {
        const label = button.querySelector('[data-add-to-cart-text]');
        const available = variant && variant.available;
        button.disabled = !available;
        if (!label) return;
        if (!variant) label.textContent = EOLUX.strings.unavailable;
        else label.textContent = available ? EOLUX.strings.addToCart : EOLUX.strings.soldOut;
      });

      if (variant && variant.featured_media) {
        const thumb = root.querySelector(`[data-media-thumb="${variant.featured_media.id}"]`);
        if (thumb) {
          thumb.click();
        } else {
          // The stacked gallery has every shot on the page already, so there is
          // nothing to switch — bring the variant's own shot into view instead.
          const stacked = root.querySelector('[data-gallery-layout="stacked"]');
          const item = stacked && stacked.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
          if (item) item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }

      if (variant) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      root.dispatchEvent(new CustomEvent('variant:change', { detail: { variant }, bubbles: true }));
    }

    picker.addEventListener('change', () => {
      const chosen = selectedOptions();
      const variant = variants.find((v) => chosen.every((value, i) => value === null || v.options[i] === value));
      render(variant || null);
    });
  }

  /** Media gallery: thumbnails swap the visible stage item. */
  function initGallery(gallery) {
    if (!once(gallery, 'galleryReady')) return;
    gallery.addEventListener('click', (e) => {
      const thumb = e.target.closest('[data-media-thumb]');
      if (!thumb) return;
      const id = thumb.dataset.mediaThumb;

      gallery.querySelectorAll('[data-media-thumb]').forEach((t) => {
        const active = t === thumb;
        t.classList.toggle('product-gallery__thumb--active', active);
        t.setAttribute('aria-selected', String(active));
      });
      gallery.querySelectorAll('[data-media-id]').forEach((item) => {
        item.hidden = item.dataset.mediaId !== id;
      });
    });
  }

  /** Selling plan choice is mirrored into every product form on the page. */
  function initSellingPlans(fieldset) {
    if (!once(fieldset, 'plansReady')) return;
    fieldset.addEventListener('change', () => {
      const checked = fieldset.querySelector('.selling-plan__input:checked');
      const value = checked ? checked.value : '';
      document.querySelectorAll('[data-selling-plan-id]').forEach((input) => { input.value = value; });
    });
  }

  /** Quantity stepper shared by the product form, sticky bar and cart. */
  function initQuantity(widget) {
    if (!once(widget, 'quantityReady')) return;
    const input = widget.querySelector('input');
    if (!input) return;
    widget.addEventListener('click', (e) => {
      const button = e.target.closest('[data-quantity-step]');
      if (!button) return;
      const next = Math.max(Number(input.min || 1), Number(input.value) + Number(button.dataset.quantityStep));
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /** "Read more" expands a clamped block. */
  function initClamp(toggle) {
    if (!once(toggle, 'clampReady')) return;
    const target = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!target) return;
    toggle.addEventListener('click', () => {
      const expanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!expanded));
      target.toggleAttribute('data-expanded', !expanded);
      toggle.hidden = !expanded ? true : false;
    });
  }

  /** The sticky purchase bar appears once the main buy button leaves the screen. */
  function initStickyBar(bar) {
    if (!once(bar, 'stickyReady')) return;
    const anchor = document.querySelector('.product-form__submit');
    if (!anchor || !('IntersectionObserver' in window)) return;

    bar.hidden = false;
    const observer = new IntersectionObserver(
      ([entry]) => {
        const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        bar.toggleAttribute('data-visible', past);
      },
      { threshold: 0 }
    );
    observer.observe(anchor);
  }

  /**
   * Product recommendations. Requests the section pre-rendered so the same
   * product-card snippet is reused rather than rebuilt in JavaScript.
   */
  function initRecommendations(root) {
    if (!once(root, 'recommendationsReady')) return;
    const { productId, limit, intent } = root.dataset;
    if (!productId) return;

    const url =
      `/recommendations/products?section_id=${root.id.replace('Recommendations-', '')}` +
      `&product_id=${productId}&limit=${limit || 4}&intent=${intent || 'related'}`;

    fetch(url)
      .then((res) => res.text())
      .then((html) => {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const fresh = parsed.querySelector('[data-recommendations-content]');
        const target = root.querySelector('[data-recommendations-content]');
        if (!fresh || !target || fresh.childElementCount === 0) {
          root.hidden = true;
          return;
        }
        target.replaceWith(fresh);
        initSection(root);
      })
      .catch(() => { root.hidden = true; });
  }

  /* ---------------------------------------------------- predictive search */
  function initPredictiveSearch(root) {
    if (!once(root, 'searchReady')) return;
    const input = root.querySelector('[data-predictive-search-input]');
    const results = root.querySelector('[data-predictive-search-results]');
    if (!input || !results) return;

    const defaultMarkup = results.innerHTML;
    let timer;
    let controller;

    input.addEventListener('input', () => {
      clearTimeout(timer);
      const query = input.value.trim();

      if (query.length < 2) {
        results.innerHTML = defaultMarkup;
        return;
      }

      timer = setTimeout(async () => {
        if (controller) controller.abort();
        controller = new AbortController();
        try {
          const url = `${EOLUX.routes.predictive_search}?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6&section_id=predictive-search`;
          const res = await fetch(url, { signal: controller.signal });
          const html = await res.text();
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const fresh = parsed.querySelector('[data-predictive-search-results]');
          if (fresh) results.innerHTML = fresh.innerHTML;
        } catch (err) {
          if (err.name !== 'AbortError') results.innerHTML = defaultMarkup;
        }
      }, 300);
    });
  }

  /* ------------------------------------------------------------------ zoom
     Full-screen viewer for the product shots. One overlay is built for the
     whole page and reused: the gallery only carries the URL of a larger
     rendition per image, so nothing heavy is fetched until someone asks to
     see it. */
  const Zoom = {
    el: null,
    img: null,
    items: [],
    index: 0,
    scale: 1,
    lastFocus: null,

    build() {
      if (Zoom.el) return;
      const s = EOLUX.strings;
      const el = document.createElement('div');
      el.className = 'zoom';
      el.setAttribute('role', 'dialog');
      el.setAttribute('aria-modal', 'true');
      el.hidden = true;
      el.innerHTML =
        '<button class="zoom__close" type="button" data-zoom-close aria-label="' + s.zoomClose + '">' +
        '<svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true"><path d="M4 4l12 12M16 4L4 16" stroke="currentColor" stroke-width="1.5" fill="none"/></svg></button>' +
        '<button class="zoom__nav zoom__nav--prev" type="button" data-zoom-prev aria-label="' + s.previous + '">' +
        '<svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true"><path d="M12 4L6 10l6 6" stroke="currentColor" stroke-width="1.5" fill="none"/></svg></button>' +
        '<button class="zoom__nav zoom__nav--next" type="button" data-zoom-next aria-label="' + s.next + '">' +
        '<svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true"><path d="M8 4l6 6-6 6" stroke="currentColor" stroke-width="1.5" fill="none"/></svg></button>' +
        '<div class="zoom__stage" data-zoom-stage><img class="zoom__image" alt=""></div>' +
        '<p class="zoom__count" data-zoom-count></p>';
      document.body.appendChild(el);

      Zoom.el = el;
      Zoom.img = el.querySelector('.zoom__image');
      const stage = el.querySelector('[data-zoom-stage]');

      el.addEventListener('click', (e) => {
        if (e.target.closest('[data-zoom-close]')) return Zoom.close();
        if (e.target.closest('[data-zoom-prev]')) return Zoom.step(-1);
        if (e.target.closest('[data-zoom-next]')) return Zoom.step(1);
        // A click on the backdrop closes; on the picture it toggles the scale.
        if (e.target === Zoom.img) return Zoom.toggleScale(e);
        if (!e.target.closest('.zoom__image')) Zoom.close();
      });

      // Drag to pan once magnified.
      let dragging = false;
      let start = { x: 0, y: 0 };
      let offset = { x: 0, y: 0 };
      stage.addEventListener('pointerdown', (e) => {
        if (Zoom.scale === 1) return;
        dragging = true;
        start = { x: e.clientX - offset.x, y: e.clientY - offset.y };
        try {
          stage.setPointerCapture(e.pointerId);
        } catch (err) {
          /* panning still works within the stage */
        }
      });
      stage.addEventListener('pointermove', (e) => {
        if (!dragging) return;
        offset = { x: e.clientX - start.x, y: e.clientY - start.y };
        Zoom.img.style.translate = offset.x + 'px ' + offset.y + 'px';
      });
      const endDrag = () => {
        dragging = false;
      };
      stage.addEventListener('pointerup', endDrag);
      stage.addEventListener('pointercancel', endDrag);
      Zoom.resetPan = () => {
        offset = { x: 0, y: 0 };
        Zoom.img.style.translate = '';
      };

      document.addEventListener('keydown', (e) => {
        if (Zoom.el.hidden) return;
        if (e.key === 'Escape') Zoom.close();
        else if (e.key === 'ArrowLeft') Zoom.step(-1);
        else if (e.key === 'ArrowRight') Zoom.step(1);
        else if (e.key === 'Tab') {
          // Keep focus inside the viewer while it is open.
          const focusable = [...Zoom.el.querySelectorAll('button')].filter((b) => b.offsetParent !== null);
          if (!focusable.length) return;
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      });
    },

    open(trigger) {
      Zoom.build();
      Zoom.lastFocus = trigger;
      // Every zoomable image in the same gallery, so the arrows walk the set.
      const gallery = trigger.closest('[data-product-gallery]') || document;
      Zoom.items = [...gallery.querySelectorAll('[data-zoom-open]')];
      Zoom.index = Math.max(0, Zoom.items.indexOf(trigger));
      Zoom.render();
      Zoom.el.hidden = false;
      document.body.style.overflow = 'hidden';
      Zoom.el.querySelector('[data-zoom-close]').focus();
    },

    render() {
      const item = Zoom.items[Zoom.index];
      if (!item) return;
      Zoom.setScale(1);
      Zoom.img.src = item.dataset.zoomSrc;
      Zoom.img.alt = item.dataset.zoomAlt || '';
      const many = Zoom.items.length > 1;
      Zoom.el.querySelector('[data-zoom-prev]').hidden = !many;
      Zoom.el.querySelector('[data-zoom-next]').hidden = !many;
      Zoom.el.querySelector('[data-zoom-count]').textContent = many ? Zoom.index + 1 + ' / ' + Zoom.items.length : '';
    },

    step(dir) {
      if (Zoom.items.length < 2) return;
      Zoom.index = (Zoom.index + dir + Zoom.items.length) % Zoom.items.length;
      Zoom.render();
    },

    setScale(value) {
      Zoom.scale = value;
      Zoom.img.style.scale = String(value);
      Zoom.el.setAttribute('data-zoom-scale', value > 1 ? 'in' : 'out');
      if (value === 1 && Zoom.resetPan) Zoom.resetPan();
    },

    toggleScale() {
      Zoom.setScale(Zoom.scale > 1 ? 1 : 2.5);
    },

    close() {
      if (!Zoom.el || Zoom.el.hidden) return;
      Zoom.el.hidden = true;
      Zoom.setScale(1);
      // Drop the source so a large rendition is not held in memory per product.
      Zoom.img.removeAttribute('src');
      document.body.style.overflow = '';
      if (Zoom.lastFocus) Zoom.lastFocus.focus();
    },
  };
  EOLUX.Zoom = Zoom;

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-zoom-open]');
    if (!trigger) return;
    e.preventDefault();
    Zoom.open(trigger);
  });

  const expertCleanups = new WeakMap();

  function initExpertTestimonials(root) {
    if (expertCleanups.has(root)) return;
    const tabs = [...root.querySelectorAll('[data-expert-tab]')];
    const panels = [...root.querySelectorAll('[data-expert-panel]')];
    const track = root.querySelector('[data-expert-track]');
    const viewport = root.querySelector('[data-expert-viewport]');
    if (!tabs.length || tabs.length !== panels.length || !track || !viewport) return;
    const controller = new AbortController();
    const { signal } = controller;
    let active = Math.max(0, tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true'));

    const positionTrack = () => {
      const visible = Math.min(3, tabs.length);
      const start = Math.min(Math.max(0, active - 1), tabs.length - visible);
      track.style.transform = `translateX(-${tabs[start].offsetLeft - tabs[0].offsetLeft}px)`;
    };
    const show = (index, focus = false) => {
      active = (index + tabs.length) % tabs.length;
      tabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', String(i === active));
        tab.tabIndex = i === active ? 0 : -1;
        panels[i].hidden = i !== active;
      });
      positionTrack();
      if (focus) tabs[active].focus({ preventScroll: true });
    };
    tabs.forEach((tab, index) => tab.addEventListener('click', () => show(index), { signal }));
    root.querySelector('[data-expert-prev]')?.addEventListener('click', () => show(active - 1), { signal });
    root.querySelector('[data-expert-next]')?.addEventListener('click', () => show(active + 1), { signal });
    track.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      if (event.key === 'Home') show(0, true);
      else if (event.key === 'End') show(tabs.length - 1, true);
      else show(active + (event.key === 'ArrowLeft' ? -1 : 1), true);
    }, { signal });
    root.closest('.shopify-section')?.addEventListener('shopify:block:select', (event) => {
      const index = tabs.findIndex((tab) => tab.dataset.blockId === event.detail?.blockId || tab.contains(event.target));
      if (index >= 0) show(index);
    }, { signal });
    const observer = new ResizeObserver(positionTrack);
    observer.observe(viewport);
    show(active);
    root.setAttribute('data-expert-ready', '');
    expertCleanups.set(root, () => {
      controller.abort();
      observer.disconnect();
      root.removeAttribute('data-expert-ready');
      expertCleanups.delete(root);
    });
  }

  const pressLogoCleanups = new WeakMap();

  function initPressLogos(root) {
    if (pressLogoCleanups.has(root)) return;
    const tabs = [...root.querySelectorAll('[data-press-tab]')];
    const quotes = [...root.querySelectorAll('[data-press-quote]')];
    const viewport = root.querySelector('[data-press-viewport]');
    const track = root.querySelector('[data-press-track]');
    const pause = root.querySelector('[data-press-pause]');
    if (!tabs.length || !viewport || !track) return;
    const controller = new AbortController();
    const { signal } = controller;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const autoplay = root.dataset.autoplay === 'true' && tabs.length > 1;
    const interval = Math.max(2000, Number(root.dataset.interval) || 4500);
    let active = 0;
    let position = tabs.length;
    let timer = 0;
    let settleTimer = 0;
    let resetFrame = 0;
    let visible = false;
    let hovered = false;
    let focused = false;
    let paused = false;
    let editorSelected = false;
    let pointerStart = null;
    let suppressClickUntil = 0;

    const schedule = () => {
      clearTimeout(timer);
      if (autoplay && visible && !paused && !hovered && !focused && !editorSelected && !document.hidden && !reducedMotion.matches) {
        timer = setTimeout(() => show(tabs.length + active + 1), interval);
      }
    };
    const normalize = () => {
      clearTimeout(settleTimer);
      cancelAnimationFrame(resetFrame);
      position = tabs.length + active;
      track.classList.add('is-resetting');
      viewport.style.setProperty('--press-position', position);
      track.getBoundingClientRect();
      resetFrame = requestAnimationFrame(() => track.classList.remove('is-resetting'));
    };
    const show = (next, manual = false) => {
      clearTimeout(settleTimer);
      position = next;
      active = ((position % tabs.length) + tabs.length) % tabs.length;
      root.querySelector('.press-logos__quotes').setAttribute('aria-live', manual ? 'polite' : 'off');
      tabs.forEach((tab, index) => {
        tab.setAttribute('aria-selected', String(index === active));
        tab.tabIndex = index === active ? 0 : -1;
        quotes[index].hidden = index !== active;
      });
      viewport.style.setProperty('--press-position', position);
      if (reducedMotion.matches) normalize();
      else settleTimer = setTimeout(normalize, 520);
      schedule();
    };

    viewport.addEventListener('click', (event) => {
      const logo = event.target.closest('[data-press-position]');
      if (!logo) return;
      if (performance.now() < suppressClickUntil) { event.preventDefault(); return; }
      show(Number(logo.dataset.pressPosition), true);
    }, { signal });
    viewport.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      let next = active + (event.key === 'ArrowLeft' ? -1 : 1);
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = tabs.length - 1;
      show(tabs.length + next, true);
      tabs[active].focus({ preventScroll: true });
    }, { signal });
    viewport.addEventListener('pointerdown', (event) => {
      if (event.isPrimary && event.button === 0) pointerStart = { x: event.clientX, y: event.clientY };
    }, { signal });
    viewport.addEventListener('pointerup', (event) => {
      if (!pointerStart) return;
      const dx = event.clientX - pointerStart.x;
      const dy = event.clientY - pointerStart.y;
      pointerStart = null;
      if (Math.abs(dx) < 40 || Math.abs(dx) <= Math.abs(dy)) return;
      suppressClickUntil = performance.now() + 300;
      show(tabs.length + active + (dx < 0 ? 1 : -1), true);
    }, { signal });
    viewport.addEventListener('pointercancel', () => { pointerStart = null; }, { signal });
    viewport.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
    root.addEventListener('pointerenter', (event) => { if (event.pointerType === 'mouse') { hovered = true; schedule(); } }, { signal });
    root.addEventListener('pointerleave', () => { hovered = false; schedule(); }, { signal });
    root.addEventListener('focusin', (event) => { focused = event.target !== pause; schedule(); }, { signal });
    root.addEventListener('focusout', (event) => { focused = root.contains(event.relatedTarget) && event.relatedTarget !== pause; schedule(); }, { signal });
    pause?.addEventListener('click', () => {
      paused = !paused;
      pause.setAttribute('aria-label', paused ? pause.dataset.playLabel : pause.dataset.pauseLabel);
      pause.querySelector('[data-press-pause-icon]').toggleAttribute('hidden', paused);
      pause.querySelector('[data-press-play-icon]').toggleAttribute('hidden', !paused);
      schedule();
    }, { signal });
    const motionChanged = () => {
      if (pause) pause.hidden = !autoplay || reducedMotion.matches;
      schedule();
    };
    reducedMotion.addEventListener('change', motionChanged, { signal });
    document.addEventListener('visibilitychange', schedule, { signal });
    const section = root.closest('.shopify-section');
    section.addEventListener('shopify:block:select', (event) => {
      const index = tabs.findIndex((tab) => tab.dataset.blockId === event.detail?.blockId || tab === event.target);
      if (index < 0) return;
      editorSelected = true;
      show(tabs.length + index, true);
    }, { signal });
    section.addEventListener('shopify:block:deselect', () => { editorSelected = false; schedule(); }, { signal });
    const observer = new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; schedule(); });
    observer.observe(root);
    root.setAttribute('data-press-ready', '');
    motionChanged();

    pressLogoCleanups.set(root, () => {
      controller.abort();
      observer.disconnect();
      clearTimeout(timer);
      clearTimeout(settleTimer);
      cancelAnimationFrame(resetFrame);
      root.removeAttribute('data-press-ready');
      track.classList.remove('is-resetting');
      pressLogoCleanups.delete(root);
    });
  }

  /* --------------------------------------------------------------- marquee
     A logo band that loops without a seam. The track is cloned until the row
     covers the viewport twice over, and every copy slides by its own width
     plus one gap — so as the first leaves, the second is exactly where it
     started. The whole thing is one CSS animation on transform; nothing here
     runs per frame. */
  const marqueeCleanups = new WeakMap();

  function initMarquee(el) {
    if (marqueeCleanups.has(el)) return;
    const track = el.querySelector('[data-marquee-track]');
    if (!track) return;
    const section = el.closest('.shopify-section');
    const pause = section?.querySelector('[data-marquee-pause]');
    const controller = new AbortController();
    const { signal } = controller;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const autoplay = el.dataset.marqueeAutoplay !== 'false';
    const canBrowse = el.hasAttribute('data-marquee-browse');
    const speed = Math.max(5, Number(el.dataset.marqueeSpeed) || 40);
    let paused = false;
    let hovered = false;
    let focused = false;
    let visible = false;
    let editorSelected = false;
    let resizeTimer = 0;

    const updateState = () => {
      const running = autoplay && !reducedMotion.matches && visible && !document.hidden && !paused && !hovered && !focused && !editorSelected;
      el.style.setProperty('--marquee-state', running ? 'running' : 'paused');
      if (pause) {
        pause.hidden = !autoplay || reducedMotion.matches;
        pause.setAttribute('aria-label', paused ? pause.dataset.playLabel : pause.dataset.pauseLabel);
        pause.querySelector('[data-marquee-pause-icon]')?.toggleAttribute('hidden', paused);
        pause.querySelector('[data-marquee-play-icon]')?.toggleAttribute('hidden', !paused);
      }
    };

    const build = () => {
      el.querySelectorAll('[data-marquee-clone]').forEach((clone) => clone.remove());
      el.removeAttribute('data-marquee-ready');
      if (!autoplay || reducedMotion.matches) { updateState(); return; }
      const width = track.scrollWidth;
      if (!width || !el.clientWidth) return;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const copies = Math.max(1, Math.ceil((el.clientWidth * 2) / (width + gap)));
      for (let i = 0; i < copies; i++) {
        const clone = track.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.inert = true;
        clone.setAttribute('data-marquee-clone', '');
        clone.removeAttribute('data-marquee-track');
        clone.querySelectorAll('[data-shopify-editor-block]').forEach((n) => n.removeAttribute('data-shopify-editor-block'));
        clone.querySelectorAll('[id]').forEach((n) => n.removeAttribute('id'));
        el.appendChild(clone);
      }
      el.style.setProperty('--marquee-duration', (Number(el.dataset.marqueeDuration) || (width + gap) / speed) + 's');
      el.setAttribute('data-marquee-ready', '');
      updateState();
    };

    const enterManual = () => {
      if (!canBrowse || el.hasAttribute('data-marquee-manual')) return;
      const transform = new DOMMatrixReadOnly(getComputedStyle(track).transform);
      const offset = el.scrollLeft - transform.m41;
      el.setAttribute('data-marquee-manual', '');
      el.scrollLeft = offset;
    };
    pause?.addEventListener('click', () => {
      paused = !paused;
      if (!paused && !editorSelected) {
        el.removeAttribute('data-marquee-manual');
        el.scrollLeft = 0;
      }
      updateState();
    }, { signal });
    el.addEventListener('pointerenter', (event) => { if (event.pointerType === 'mouse') { hovered = true; updateState(); } }, { signal });
    el.addEventListener('pointerleave', () => { hovered = false; updateState(); }, { signal });
    el.addEventListener('focusin', () => { focused = true; updateState(); }, { signal });
    el.addEventListener('focusout', (event) => { focused = el.contains(event.relatedTarget); updateState(); }, { signal });
    if (canBrowse) {
      el.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'touch') return;
        enterManual(); paused = true; updateState();
      }, { signal, passive: true });
      el.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        enterManual(); paused = true; updateState();
        const step = (track.firstElementChild?.getBoundingClientRect().width || 280) + 24;
        if (event.key === 'Home') el.scrollLeft = 0;
        else if (event.key === 'End') el.scrollLeft = el.scrollWidth;
        else el.scrollLeft += event.key === 'ArrowLeft' ? -step : step;
      }, { signal });
      section?.addEventListener('shopify:section:select', () => { editorSelected = true; updateState(); }, { signal });
      section?.addEventListener('shopify:block:select', (event) => {
        const block = [...track.children].find((item) => item.dataset.blockId === event.detail?.blockId);
        if (!block) return;
        editorSelected = true; enterManual(); updateState();
        el.scrollLeft += block.getBoundingClientRect().left - el.getBoundingClientRect().left - 24;
      }, { signal });
      const editorDeselect = () => {
        editorSelected = false;
        if (!paused) { el.removeAttribute('data-marquee-manual'); el.scrollLeft = 0; }
        updateState();
      };
      section?.addEventListener('shopify:block:deselect', editorDeselect, { signal });
      section?.addEventListener('shopify:section:deselect', editorDeselect, { signal });
    }
    track.querySelectorAll('img').forEach((img) => {
      img.addEventListener('load', build, { once: true, signal });
      img.addEventListener('error', build, { once: true, signal });
    });
    const resize = new ResizeObserver(() => { clearTimeout(resizeTimer); resizeTimer = setTimeout(build, 100); });
    resize.observe(el);
    resize.observe(track);
    const observer = new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; updateState(); });
    observer.observe(el);
    reducedMotion.addEventListener('change', build, { signal });
    document.addEventListener('visibilitychange', updateState, { signal });
    build();
    marqueeCleanups.set(el, () => {
      controller.abort(); observer.disconnect(); resize.disconnect(); clearTimeout(resizeTimer);
      el.querySelectorAll('[data-marquee-clone]').forEach((clone) => clone.remove());
      el.removeAttribute('data-marquee-ready');
      el.removeAttribute('data-marquee-manual');
      el.style.removeProperty('--marquee-state');
      marqueeCleanups.delete(el);
    });
  }

  /* --------------------------------------------------------- scroll reveal
     One observer for the whole page, and each element is unobserved the moment
     it arrives: the work is done once and then costs nothing for the rest of
     the visit. Nothing here runs on scroll.

     Grids inside a section stagger their own children, so a row of product
     cards arrives in sequence instead of all at once. */
  const REVEAL_GROUPS =
    '.main-collection__grid, .featured-products__grid, .blog-posts__grid, .main-blog__grid,' +
    '.product-recommendations__grid, .main-search__grid, .banner-pair__grid, .shop-by-need__grid,' +
    '.downloads__grid, .professional-links__grid';

  let revealObserver = null;
  let revealPending = null;

  function reveal(el, instant) {
    if (instant) el.setAttribute('data-revealed-instant', '');
    el.setAttribute('data-revealed', '');

    // Stagger the cards this section owns, capped so a long grid still lands
    // quickly.
    el.querySelectorAll(REVEAL_GROUPS).forEach((group) => {
      [...group.children].forEach((child, i) => {
        if (child.hasAttribute('data-revealed')) return;
        child.setAttribute('data-reveal-item', '');
        child.style.setProperty('--reveal-delay', Math.min(i * 60, 400) + 'ms');
        // Two frames: one for the initial state to be styled, one to animate.
        requestAnimationFrame(() => requestAnimationFrame(() => child.setAttribute('data-revealed', '')));
      });
    });
  }

  function initReveal(root) {
    const targets = root.querySelectorAll('[data-reveal]:not([data-revealed])');
    if (!targets.length) return;

    // Hiding is opt-in and only ever done by this script, so a blocked bundle
    // leaves the page fully visible.
    document.documentElement.classList.add('js-reveal');

    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      targets.forEach((el) => reveal(el, true));
      return;
    }

    if (!revealObserver) {
      /**
       * Anything the visitor has already scrolled past must be visible, even
       * if it was never seen intersecting.
       *
       * IntersectionObserver reports *changes*. Jump from the middle of a page
       * to the bottom — an anchor link, a restored scroll position, a flick on
       * a trackpad — and every section in between goes from "not intersecting,
       * below" to "not intersecting, above" without ever changing state, so no
       * callback fires and they stay invisible for the rest of the visit. This
       * sweep runs on each callback and clears whatever the jump skipped.
       */
      revealPending = new Set();
      const flush = () => {
        const height = window.innerHeight || document.documentElement.clientHeight;
        revealPending.forEach((el) => {
          if (el.getBoundingClientRect().top < height) {
            revealPending.delete(el);
            revealObserver.unobserve(el);
            reveal(el, true);
          }
        });
      };

      revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            revealPending.delete(entry.target);
            revealObserver.unobserve(entry.target);
            reveal(entry.target, false);
          });
          flush();
        },
        // Start a little before the section's edge so it is already settling by
        // the time it is properly on screen.
        { rootMargin: '0px 0px -8% 0px', threshold: 0.01 }
      );

      window.addEventListener('resize', flush, { passive: true });
      window.addEventListener('pageshow', flush);
    }

    const vh = window.innerHeight || document.documentElement.clientHeight;
    targets.forEach((el) => {
      // Above the fold at first paint: show it now, without a transition.
      if (el.getBoundingClientRect().top < vh * 0.9) {
        reveal(el, true);
      } else {
        revealPending.add(el);
        revealObserver.observe(el);
      }
    });
  }

  /* ------------------------------------------------------- before / after
     The divider follows the section's travel through the viewport, so the
     comparison plays itself as the visitor scrolls. The moment they grab it,
     scrolling stops driving it — nothing is more annoying than a control that
     fights you. Drag, touch and arrow keys all work. */
  const compareCleanups = new WeakMap();

  function initCompare(root) {
    if (compareCleanups.has(root)) return;

    const frame = root.querySelector('[data-ba-frame]');
    const handle = root.querySelector('[data-ba-handle]');
    if (!frame || !handle) return;

    const from = Number(root.dataset.baFrom || 25);
    const to = Number(root.dataset.baTo || 75);
    let owned = false;
    const controller = new AbortController();
    const { signal } = controller;
    let observer;
    let animationFrame = 0;
    let running = false;
    compareCleanups.set(root, () => {
      running = false;
      controller.abort();
      observer?.disconnect();
      cancelAnimationFrame(animationFrame);
      compareCleanups.delete(root);
    });

    const set = (pct) => {
      const value = Math.min(100, Math.max(0, pct));
      root.style.setProperty('--ba-split', value + '%');
      // A second, unitless copy so the labels can fade with the wipe in CSS.
      root.style.setProperty('--ba-split-num', String(Math.round(value)));
      handle.setAttribute('aria-valuenow', String(Math.round(value)));
    };

    const pctFromPointer = (clientX) => {
      const rect = frame.getBoundingClientRect();
      return ((clientX - rect.left) / rect.width) * 100;
    };

    let dragging = false;
    frame.addEventListener('pointerdown', (e) => {
      dragging = true;
      owned = true;
      // Move first, capture second: setPointerCapture throws on a pointer the
      // browser no longer considers active, and losing the drag to that would
      // be far worse than losing the capture.
      set(pctFromPointer(e.clientX));
      try {
        frame.setPointerCapture(e.pointerId);
      } catch (err) {
        /* dragging still works, it just stops at the frame's edge */
      }
    }, { signal });
    frame.addEventListener('pointermove', (e) => {
      if (dragging) set(pctFromPointer(e.clientX));
    }, { signal });
    const stop = (e) => {
      dragging = false;
      try {
        if (e.pointerId != null && frame.hasPointerCapture(e.pointerId)) frame.releasePointerCapture(e.pointerId);
      } catch (err) {
        /* nothing to release */
      }
    };
    frame.addEventListener('pointerup', stop, { signal });
    frame.addEventListener('pointercancel', stop, { signal });

    handle.addEventListener('keydown', (e) => {
      const step = e.shiftKey ? 10 : 2;
      const current = Number(handle.getAttribute('aria-valuenow'));
      if (e.key === 'ArrowLeft') set(current - step);
      else if (e.key === 'ArrowRight') set(current + step);
      else if (e.key === 'Home') set(0);
      else if (e.key === 'End') set(100);
      else return;
      owned = true;
      e.preventDefault();
    }, { signal });

    // Scroll driving is decoration, so it is skipped when motion is reduced.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      set(from);
      return;
    }

    const track = () => {
      const rect = frame.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      // 0 as the frame enters from the bottom, 1 as it leaves past the top.
      const progress = Math.min(1, Math.max(0, (vh - rect.top) / (vh + rect.height)));
      set(from + (to - from) * progress);
    };

    // A rAF loop rather than a scroll listener: scroll events do not fire for
    // every scroll container, are skipped during some smooth-scroll and
    // momentum animations, and are missed entirely in a few embedded contexts.
    // Reading the rect each frame is cheap and always right. The loop only
    // turns over while the frame is near the viewport, and stops for good once
    // the visitor takes the handle.
    const loop = () => {
      if (!running || owned) {
        running = false;
        return;
      }
      track();
      animationFrame = requestAnimationFrame(loop);
    };

    observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.some((entry) => entry.isIntersecting);
        if (visible && !running && !owned) {
          running = true;
          animationFrame = requestAnimationFrame(loop);
        } else if (!visible) {
          running = false;
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(frame);
    track();
  }

  /* ----------------------------------------------------------------- reel
     A horizontal band of story clips. Scrolling is native — the arrows only
     exist for pointers, and they stay hidden unless there is somewhere to go,
     so a reel with three clips on a wide screen shows no controls at all. */
  function initReel(track) {
    if (!once(track, 'reelReady')) return;

    const inner = track.parentElement;
    const prev = inner.querySelector('[data-reel-prev]');
    const next = inner.querySelector('[data-reel-next]');
    if (!prev || !next) return;

    const page = () => {
      // Measure the band's own first card and its own gap rather than assuming
      // the video reel's, so any section can reuse this track.
      const card = track.firstElementChild;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const step = card ? card.getBoundingClientRect().width + gap : track.clientWidth;
      // Advance by whole cards, never leaving one half-visible mid-band.
      return Math.max(1, Math.floor(track.clientWidth / step)) * step;
    };

    const sync = () => {
      const scrollable = track.scrollWidth - track.clientWidth > 4;
      prev.hidden = !scrollable || track.scrollLeft <= 4;
      next.hidden = !scrollable || track.scrollLeft >= track.scrollWidth - track.clientWidth - 4;
    };

    prev.addEventListener('click', () => track.scrollBy({ left: -page(), behavior: 'smooth' }));
    next.addEventListener('click', () => track.scrollBy({ left: page(), behavior: 'smooth' }));
    track.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);

    sync();
    // Posters change the track width as they decode.
    track.querySelectorAll('img').forEach((img) => {
      if (!img.complete) img.addEventListener('load', sync, { once: true });
    });
  }

  const reelVideoCleanups = new WeakMap();

  function initReelVideo(frame) {
    if (reelVideoCleanups.has(frame)) return;
    const player = frame.querySelector('video');
    const toggle = frame.querySelector('[data-reel-toggle]');
    const sound = frame.querySelector('[data-reel-sound]');
    if (!player || !toggle || !sound) return;

    const controller = new AbortController();
    const { signal } = controller;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let visible = false;
    let manuallyPaused = false;
    let manuallyStarted = false;
    let pointerStart = null;
    let dragged = false;

    player.controls = false;
    player.muted = true;
    player.pause();

    const syncPlaybackUI = () => {
      const playing = !player.paused && !player.ended;
      frame.toggleAttribute('data-playing', playing);
      toggle.setAttribute('aria-label', playing ? toggle.dataset.pauseLabel : toggle.dataset.playLabel);
    };
    const syncSoundUI = () => {
      const muted = player.muted || player.volume === 0;
      sound.setAttribute('aria-label', muted ? sound.dataset.unmuteLabel : sound.dataset.muteLabel);
      sound.querySelector('[data-reel-muted-icon]').toggleAttribute('hidden', !muted);
      sound.querySelector('[data-reel-audible-icon]').toggleAttribute('hidden', muted);
    };
    const shouldPlay = () => visible && !document.hidden && !manuallyPaused &&
      (!reducedMotion.matches || manuallyStarted);
    const syncPlayback = () => {
      if (shouldPlay()) {
        if (player.paused) player.play().catch(syncPlaybackUI);
      } else {
        player.pause();
      }
      syncPlaybackUI();
    };

    toggle.addEventListener('pointerdown', (event) => {
      pointerStart = { x: event.clientX, y: event.clientY };
      dragged = false;
    }, { signal, passive: true });
    toggle.addEventListener('pointermove', (event) => {
      if (pointerStart && Math.hypot(event.clientX - pointerStart.x, event.clientY - pointerStart.y) > 10) {
        dragged = true;
      }
    }, { signal, passive: true });
    toggle.addEventListener('pointercancel', () => { dragged = true; pointerStart = null; }, { signal });
    toggle.addEventListener('click', (event) => {
      if (event.detail && dragged) return;
      manuallyPaused = !player.paused;
      manuallyStarted = !manuallyPaused;
      syncPlayback();
    }, { signal });

    sound.addEventListener('click', () => {
      const unmute = player.muted || player.volume === 0;
      if (unmute) {
        document.querySelectorAll('[data-reel-video] video').forEach((other) => {
          if (other !== player) other.muted = true;
        });
        player.volume = 1;
      }
      player.muted = !unmute;
      syncSoundUI();
    }, { signal });

    player.addEventListener('play', () => {
      if (!shouldPlay()) player.pause();
      syncPlaybackUI();
    }, { signal });
    player.addEventListener('pause', syncPlaybackUI, { signal });
    player.addEventListener('ended', syncPlaybackUI, { signal });
    player.addEventListener('volumechange', syncSoundUI, { signal });
    document.addEventListener('visibilitychange', syncPlayback, { signal });
    reducedMotion.addEventListener('change', syncPlayback, { signal });

    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting && entry.intersectionRatio >= 0.2;
      syncPlayback();
    }, { threshold: [0, 0.2] });
    observer.observe(frame);
    syncPlaybackUI();
    syncSoundUI();

    reelVideoCleanups.set(frame, () => {
      observer.disconnect();
      controller.abort();
      player.pause();
      frame.removeAttribute('data-playing');
      reelVideoCleanups.delete(frame);
    });
  }

  /* ---------------------------------------------------------------- video
     Cards show a poster until they are asked to play, so a reel of six clips
     costs one poster image each instead of six video streams. */
  function initVideoCard(frame) {
    if (!once(frame, 'videoReady')) return;

    const trigger = frame.querySelector('[data-video-play]');
    const player = frame.querySelector('video');

    if (trigger && player) {
      trigger.addEventListener('click', () => {
        // Pause any other clip first: two videos playing at once on the same
        // page is never what the visitor asked for.
        document.querySelectorAll('[data-video] video').forEach((other) => {
          if (other !== player) other.pause();
        });
        player.setAttribute('controls', '');
        player.play().catch(() => {});
        frame.setAttribute('data-playing', '');
        trigger.remove();
      });
    }

    // A hosted embed is only fetched once the visitor commits to watching it.
    const embed = frame.querySelector('[data-video-embed]');
    if (embed) {
      embed.addEventListener('click', () => {
        const id = embed.dataset.videoEmbed;
        const type = embed.dataset.videoType;
        if (!id) return;
        const src =
          type === 'vimeo'
            ? `https://player.vimeo.com/video/${id}?autoplay=1`
            : `https://www.youtube.com/embed/${id}?autoplay=1`;
        const iframe = document.createElement('iframe');
        iframe.src = src;
        iframe.className = 'video-card__embed';
        iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
        iframe.setAttribute('allowfullscreen', '');
        iframe.setAttribute('title', embed.textContent.trim() || 'video');
        frame.appendChild(iframe);
        frame.setAttribute('data-playing', '');
        embed.remove();
      });
    }
  }

  /* ----------------------------------------------------------- price range
     Two range inputs over one track. Each handle pushes rather than crosses the
     other, and the number fields below stay the authoritative pair — they are
     what the form actually submits, so typing into them moves the handles and
     dragging a handle writes back. */
  function initPriceRange(root) {
    if (!once(root, 'priceReady')) return;

    const from = root.querySelector('[data-price-handle="from"]');
    const to = root.querySelector('[data-price-handle="to"]');
    const fieldFrom = root.querySelector('[data-price-field="from"]');
    const fieldTo = root.querySelector('[data-price-field="to"]');
    if (!from || !to || !fieldFrom || !fieldTo) return;

    const max = Number(root.dataset.max) || Number(to.max) || 0;
    if (!max) return;

    const paint = () => {
      const lo = Number(from.value);
      const hi = Number(to.value);
      root.style.setProperty('--price-from', `${(lo / max) * 100}%`);
      root.style.setProperty('--price-to', `${(hi / max) * 100}%`);
    };

    const fromHandles = () => {
      // Keep the pair ordered without letting one handle jump past the other.
      if (Number(from.value) > Number(to.value)) {
        if (document.activeElement === from) from.value = to.value;
        else to.value = from.value;
      }
      // An untouched end stays empty so the filter is not applied at all.
      fieldFrom.value = Number(from.value) === 0 ? '' : from.value;
      fieldTo.value = Number(to.value) === max ? '' : to.value;
      paint();
    };

    const fromFields = () => {
      const lo = fieldFrom.value === '' ? 0 : Number(fieldFrom.value);
      const hi = fieldTo.value === '' ? max : Number(fieldTo.value);
      from.value = Math.min(Math.max(lo, 0), max);
      to.value = Math.min(Math.max(hi, 0), max);
      paint();
    };

    from.addEventListener('input', fromHandles);
    to.addEventListener('input', fromHandles);
    fieldFrom.addEventListener('input', fromFields);
    fieldTo.addEventListener('input', fromFields);

    // The handles only become operable once this runs, so they stay out of the
    // tab order in the markup and are put back here.
    from.tabIndex = 0;
    to.tabIndex = 0;
    root.querySelector('.facets__slider')?.removeAttribute('aria-hidden');
    from.setAttribute('aria-label', fieldFrom.previousElementSibling?.textContent?.trim() || 'min');
    to.setAttribute('aria-label', fieldTo.previousElementSibling?.textContent?.trim() || 'max');

    paint();
  }

  /* --------------------------------------------------------- sticky column
     Keeps the buy column with the gallery while the gallery scrolls.

     This cannot be `position: sticky`. Sticky with `top` pins the column's top
     immediately, and this column is taller than the viewport — the payment
     buttons at its foot would never be reachable. Sticky with `bottom` is the
     mirror of `top`: it shifts an element UP when it sits too low, and what is
     needed here is a shift DOWN once the column has been read in full. So the
     offset is applied by hand.

     The rule is one line: the column scrolls normally until its bottom edge
     reaches the fold, then it holds there. Scrolling back up releases it. */
  function initStickyColumn(el) {
    if (!once(el, 'stickyColumnReady')) return;

    const container = el.parentElement;
    if (!container) return;

    const GAP = 24;
    const MIN_WIDTH = 990;
    let running = false;
    let offsetTopWithin = 0;

    const measure = () => {
      // Read the natural offset with the transform cleared, or it compounds.
      el.style.transform = '';
      offsetTopWithin = el.getBoundingClientRect().top - container.getBoundingClientRect().top;
    };

    const track = () => {
      if (innerWidth < MIN_WIDTH) {
        el.style.transform = '';
        return;
      }

      const cRect = container.getBoundingClientRect();
      const h = el.offsetHeight;
      const natTop = cRect.top + offsetTopWithin;

      // Hold the bottom edge at the fold once it gets there; never move up.
      const held = Math.max(natTop, innerHeight - GAP - h);
      let offset = held - natTop;

      // And never past the end of the column's own grid area.
      offset = Math.min(offset, cRect.height - offsetTopWithin - h);
      offset = Math.max(offset, 0);

      el.style.transform = offset > 0 ? `translate3d(0, ${Math.round(offset)}px, 0)` : '';
    };

    const loop = () => {
      if (!running) return;
      track();
      requestAnimationFrame(loop);
    };

    // rAF rather than a scroll listener: scroll events are skipped during
    // momentum and smooth-scroll, and the column would lag behind the gallery.
    new IntersectionObserver(
      (entries) => {
        const visible = entries.some((e) => e.isIntersecting);
        if (visible && !running) {
          running = true;
          requestAnimationFrame(loop);
        } else if (!visible) {
          running = false;
        }
      },
      { rootMargin: '200px' }
    ).observe(container);

    addEventListener('resize', () => {
      measure();
      track();
    });

    measure();
    track();
  }

  /* ----------------------------------------------------------- article toc */
  function initArticleToc(layout) {
    if (!once(layout, 'articleTocReady')) return;

    const list = layout.querySelector('[data-toc-list]');
    const source = layout.querySelector('[data-toc-source]');
    if (!list || !source) return;

    // Merchants pick heading levels freely in the rich text editor, and some
    // published articles use h1 for their sections. Take the shallowest level
    // that is actually present rather than assuming h2.
    const headings = ['h1', 'h2', 'h3']
      .map((tag) => [...source.querySelectorAll(tag)].filter((h) => h.textContent.trim()))
      .find((group) => group.length) || [];

    // A merchant writing flat prose gets no rail rather than an empty card.
    if (!headings.length) {
      layout.setAttribute('data-toc-empty', '');
      return;
    }

    list.replaceChildren(
      ...headings.map((heading, i) => {
        // Article bodies come from the rich text editor, so ids are rarely
        // there already — mint a stable one before linking to it.
        if (!heading.id) heading.id = `article-section-${i + 1}`;

        const link = document.createElement('a');
        link.href = `#${heading.id}`;
        link.textContent = heading.textContent.trim();

        const item = document.createElement('li');
        item.append(link);
        return item;
      })
    );
  }

  /* ------------------------------------------------------------ init pass */
  function initSection(root) {
    root.querySelectorAll('[data-header]').forEach(initHeader);
    root.querySelectorAll('[data-press-logos]').forEach(initPressLogos);
    root.querySelectorAll('[data-expert-testimonials]').forEach(initExpertTestimonials);
    root.querySelectorAll('[data-marquee]').forEach(initMarquee);
    initReveal(root);
    root.querySelectorAll('[data-ba-compare]').forEach(initCompare);
    root.querySelectorAll('[data-reel-track]').forEach(initReel);
    root.querySelectorAll('[data-reel-video]').forEach(initReelVideo);
    // Keyed off the attribute, not one section's class, so any card that shows
    // a poster until it is asked to play gets the same behaviour.
    root.querySelectorAll('[data-video]').forEach(initVideoCard);
    root.querySelectorAll('[data-drawer]').forEach(initDrawer);
    root.querySelectorAll('[data-price-range]').forEach(initPriceRange);
    root.querySelectorAll('.header__nav-item--has-menu').forEach(initDropdown);
    root.querySelectorAll('[data-accordion-trigger]').forEach(initAccordion);
    root.querySelectorAll('[data-carousel]').forEach(initCarousel);
    root.querySelectorAll('[data-product-root]').forEach(initProduct);
    root.querySelectorAll('[data-product-gallery]').forEach(initGallery);
    root.querySelectorAll('[data-selling-plans]').forEach(initSellingPlans);
    root.querySelectorAll('[data-quantity]').forEach(initQuantity);
    root.querySelectorAll('[data-clamp-toggle]').forEach(initClamp);
    root.querySelectorAll('[data-sticky-bar]').forEach(initStickyBar);
    root.querySelectorAll('[data-sticky-column]').forEach(initStickyColumn);
    root.querySelectorAll('[data-article-toc]').forEach(initArticleToc);
    root.querySelectorAll('.footer').forEach(initFooterMenus);
    root.querySelectorAll('[data-recommendations]').forEach(initRecommendations);
    root.querySelectorAll('[data-predictive-search]').forEach(initPredictiveSearch);
    syncHowToUseLinks();
    document.dispatchEvent(new CustomEvent('eolux:init', { detail: { root } }));
  }
  EOLUX.initSection = initSection;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initSection(document));
  } else {
    initSection(document);
  }

  /* -------------------------------------------------- theme editor events */
  document.addEventListener('shopify:section:load', (e) => initSection(e.target));
  document.addEventListener('shopify:section:reorder', () => syncHowToUseLinks());
  document.addEventListener('shopify:section:unload', (e) => {
    syncHowToUseLinks(e.target);
    Drawer.closeAll();
    e.target.querySelectorAll('.header__nav-item--has-menu').forEach((item) => dropdownCleanups.get(item)?.());
    e.target.querySelectorAll('[data-header]').forEach((header) => headerCleanups.get(header)?.());
    e.target.querySelectorAll('[data-press-logos]').forEach((root) => pressLogoCleanups.get(root)?.());
    e.target.querySelectorAll('[data-expert-testimonials]').forEach((root) => expertCleanups.get(root)?.());
    e.target.querySelectorAll('[data-marquee]').forEach((root) => marqueeCleanups.get(root)?.());
    e.target.querySelectorAll('[data-reel-video]').forEach((frame) => reelVideoCleanups.get(frame)?.());
    e.target.querySelectorAll('[data-ba-compare]').forEach((root) => compareCleanups.get(root)?.());
  });
  document.addEventListener('shopify:block:select', (e) => {
    const drawer = e.target.closest('[data-drawer]');
    if (drawer) Drawer.open(drawer);

    // Bring the selected slide into view so editing a carousel block shows it.
    const carousel = e.target.closest('[data-carousel]');
    if (carousel && carousel.carouselShow) {
      const slides = [...carousel.querySelectorAll('[data-carousel-slide]')];
      const i = slides.indexOf(e.target.closest('[data-carousel-slide]'));
      if (i >= 0) carousel.carouselShow(i);
    }
  });
})();
